'use client';

import { useSyncExternalStore } from 'react';
import { DEFAULT_VIEWS_SORT, VIEWS_SORTS, type ViewsSort } from './nav-views-sort';

/**
 * The views sort preference as a client hook: the one stored choice the
 * sidebar's Views and Boards sections and the two overviews (/boards,
 * /views) order by (`sortNavViews`). A personal, client-only preference: it
 * rides localStorage, never the log, and a same-tab change is broadcast with
 * an event, so every surface that reads it follows a change made in the
 * sidebar's sort menu.
 *
 * The key and the event live here only: the sidebar's sort menu writes
 * through `setViewsSort`, and every surface reads through `useViewsSort`. The
 * server snapshot is the default, which the server can order by too, since
 * `lastViewedAt` rides in as data: first paint is stable, and the client
 * re-reads after mount.
 */
const VIEWS_SORT_KEY = 'wg_views_sort';
const VIEWS_SORT_EVENT = 'wg:views-sort';

function subscribe(onChange: () => void): () => void {
  window.addEventListener('storage', onChange);
  window.addEventListener(VIEWS_SORT_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(VIEWS_SORT_EVENT, onChange);
  };
}

function read(): ViewsSort {
  try {
    const v = localStorage.getItem(VIEWS_SORT_KEY);
    return VIEWS_SORTS.includes(v as ViewsSort) ? (v as ViewsSort) : DEFAULT_VIEWS_SORT;
  } catch {
    // Storage unavailable (a private window): the default order.
    return DEFAULT_VIEWS_SORT;
  }
}

export function useViewsSort(): ViewsSort {
  return useSyncExternalStore(subscribe, read, () => DEFAULT_VIEWS_SORT);
}

export function setViewsSort(value: ViewsSort): void {
  try {
    localStorage.setItem(VIEWS_SORT_KEY, value);
  } catch {
    // Storage unavailable: the choice cannot be kept, and the read stays the default.
  }
  window.dispatchEvent(new Event(VIEWS_SORT_EVENT));
}
